import { AxiosError, AxiosResponse } from 'axios';
import {
  FinancingPostData as FinancingPost,
  PaymentMethod
} from '~/App/shared/actions/checkoutActions';
import { DeliveryLocation } from '~/App/shared/interfaces/DeliveryLocation';
import { FacilityTransportCost } from '~/App/shared/interfaces/FacilityTransportCost';
import {
  CheckoutBuyer,
  CheckoutOrder,
  PickupProxyPerson,
  PickupProxyTransportCompany
} from '~/App/shared/interfaces/store/Checkout';
import { CheckoutType } from '~/App/shared/types/CheckoutType';
import { PICKUP_LOCATIONS } from '~/App/views/Checkout/constants';
import { orchestration } from '~/config/public';
import axios from '~/helpers/orchestration/axiosWithAuthHandling';

interface StartCheckoutOrder {
  idToken?: string | undefined;
  auctionId: string;
  checkoutType: CheckoutType;
}

export const startCheckoutOrder = async ({
  idToken: _idToken,
  auctionId,
  checkoutType
}: StartCheckoutOrder) => {
  return await axios.post<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/start`,
    {
      start: {
        auctionId,
        checkoutType
      }
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

export const getCheckoutOrderByAuctionId = async ({
  idToken: _idToken,
  auctionId,
  checkoutType
}: StartCheckoutOrder) => {
  return await axios.get<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/by_auction_id`,
    {
      params: {
        checkoutType,
        auctionId
      },
      responseType: 'json',
      withCredentials: true
    }
  );
};

interface GetOrderRequest {
  orderId: string;
  idToken: string;
  authToken?: string;
}
interface GetOrderResponse {
  order: CheckoutOrder;
}
export const getOrder = ({
  orderId,
  idToken: _idToken,
  authToken
}: GetOrderRequest) => {
  return axios.get<GetOrderResponse>(
    `${orchestration.url}/delivery/orders/${orderId}`,
    {
      withCredentials: true,
      params: { authToken }
    }
  );
};

interface GetOrderIdRequest {
  idToken: string;
  auctionId: string;
  orderType: CheckoutType;
}
export const getOrderIdFromAuction = async ({
  auctionId,
  idToken: _idToken,
  orderType
}: GetOrderIdRequest) => {
  return await axios.post(
    `${orchestration.url}/delivery/order`,
    {
      auctionId,
      orderType
    },
    {
      withCredentials: true
    }
  );
};

export interface CheckoutHomeDelivery {
  available: boolean;
  missingMeasurement?: boolean;

  toFacilityId?: string | null;
  toFacilityName?: string | null;

  homeDeliveryCost?: number;
  transportToFacilityCost?: string;
}

export type RulesMessage = {
  message: string;
  messageType: 'ERROR' | 'WARNING' | 'INFO';
};

export type GetTransportOptionsResponse = {
  generalRulesMessages?: RulesMessage[];
  options: {
    rulesMessages?: RulesMessage[];
    transportInformation: {
      optionId: string;
      anonymizedTransportCompanyName: string;
      leadTime: string;
      listDescription: string;
      destinationCountry: string;
      destinationCountryCode: string;
      destinationAddress?: {
        city: string | null;
        zipCode: number | null;
        zipCodeStr: string | null;
        streetName: string | null;
        streetNumber: string | null;
        country: string | null;
        facilityId: string | null;
        facilityName: string | null;
      };
    };
    priceDetails: {
      totalExclVat: string;
      totalVat: string;
      roundedTotalInclVat: string;
      currency: string;
    };
  }[];
};

export const getTransportOptions = async (
  orderId: number | string,
  idToken: string,
  address?: DeliveryLocation
) => {
  return await axios.post<GetTransportOptionsResponse>(
    `${orchestration.url}/delivery/transport/checkout/options`,
    {
      deliveryOrderId: orderId.toString(),
      ...(address ? { destinationAddress: address } : {})
    },
    {
      responseType: 'json',
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    }
  );
};

export const updateCheckoutFinancing = async (
  orderId: number,
  _idToken: string,
  financing: FinancingPost
) => {
  return await axios.post<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/${orderId}/financing`,
    {
      financing
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

export const updateCheckoutHeavyEquipmentFinancing = async (
  orderId: number,
  _idToken: string,
  data: { paymentMethod: PaymentMethod; interestInFinancing: boolean }
) => {
  return await axios.post<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/${orderId}/financing`,
    {
      financing: {
        paymentMethod: data.paymentMethod,
        interestInFinancing: data.interestInFinancing,
        warranty: null,
        insurance: null,
        warrantyPeriod: null,
        serviceAgreement: null
      }
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

export interface CheckoutConfigureCarDealerPayload {
  introduction: {
    shownToOrderingMember: boolean;
    acceptsExtraTires: boolean;
  };
}

export const updateCheckoutConfigureCarDealer = async (
  orderId: number,
  _idToken: string,
  payload: CheckoutConfigureCarDealerPayload
) =>
  await axios.post<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/${orderId}/introduction`,
    payload,
    {
      responseType: 'json',
      withCredentials: true
    }
  );

export const updateCheckoutBuyer = async (
  orderId: number,
  _idToken: string,
  buyer: CheckoutBuyer
) => {
  return await axios.post<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/${orderId}/buyer`,
    {
      buyer
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

interface DeliveryFacility {
  destinationFacilityId: string;
}

export interface DeliveryHeavyEquipment {
  pickupMethod: PICKUP_LOCATIONS;
}

export const updateCheckoutDelivery = (
  orderId: number,
  _idToken: string,
  transport:
    | {
        deliveryLocation: DeliveryLocation | null;
        transportOptionId?: string | null;
      }
    | DeliveryFacility
    | DeliveryHeavyEquipment
) => {
  return axios.post<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/${orderId}/transport`,
    {
      transport
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};
export const updateCheckoutPickupBy = (
  orderId: number,
  _idToken: string,
  pickupByBuyer: boolean
) => {
  return axios.post(
    `${orchestration.url}/delivery/orders/${orderId}/pickup`,
    {
      pickup: { pickupByBuyer }
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

type UpdatePickupProxyResponse = {
  order: {
    pickupProxy: PickupProxyPerson;
  };
};

export interface SetPickupPersonCarDealerInput {
  authToken?: string;
  deliveryPolicyAccepted?: boolean;
  pickupProxyPolicyAccepted?: boolean;
  pickupProxyPerson: PickupProxyPerson | null;
  pickupProxyTransportCompany: PickupProxyTransportCompany | null;
}
export const updatePickupProxy = (
  orderId: number,
  _idToken: string,
  data: SetPickupPersonCarDealerInput
) => {
  return axios.post<UpdatePickupProxyResponse>(
    `${orchestration.url}/delivery/orders/${orderId}/pickup-proxy`,
    data,
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

const toFacilityTransportCosts = (
  options: GetTransportOptionsResponse['options']
): FacilityTransportCost[] =>
  options.reduce<FacilityTransportCost[]>((costs, option) => {
    const { optionId, destinationAddress } = option.transportInformation;

    if (!destinationAddress?.facilityId) {
      return costs;
    }

    return [
      ...costs,
      {
        optionId,
        toFacilityId: destinationAddress.facilityId,
        price: parseInt(option.priceDetails.roundedTotalInclVat, 10),
        currency: option.priceDetails.currency
      }
    ];
  }, []);

export const getTransportCosts = async (
  orderId: number | string,
  idToken: string
) => {
  const { data } = await getTransportOptions(orderId, idToken);

  return toFacilityTransportCosts(data.options);
};

export type WarrantyChoice = {
  paymentMethod: PaymentMethod;
  price: number;
  period: number;
  originalPrice: number;
  monthlyCost?: null | number;
};

export type CheckoutPricesResponse = AxiosResponse & {
  INSURANCE: {
    available: boolean;
    included: boolean;
    price?: number;
  };
  SERVICE_AGREEMENT: {
    available: boolean;
    included: boolean;
    price?: number;
  };
  WARRANTY: {
    available: boolean;
    included: boolean;
    choices: WarrantyChoice[];
  };
};

export interface FetchCheckoutPricesInput {
  orderId: string;
  idToken: string;
  paymentMethod: PaymentMethod;
  paymentPeriod: number;
  downPaymentPercent: number;
}
export const fetchCheckoutPrices = ({
  orderId,
  idToken: _idToken,
  paymentMethod,
  paymentPeriod,
  downPaymentPercent
}: FetchCheckoutPricesInput) => {
  const paymentPeriodKey =
    paymentMethod === 'LOAN' ? 'loanPeriod' : 'leasingPeriod';

  return axios.get<CheckoutPricesResponse>(
    `${orchestration.url}/delivery/orders/${orderId}/service_availability`,
    {
      params: {
        paymentMethod,
        [paymentPeriodKey]:
          paymentMethod === 'CASH' ? undefined : paymentPeriod,
        downPaymentPercent
      },
      responseType: 'json',
      withCredentials: true
    }
  );
};

export const checkoutSubmit = async (
  orderId: number,
  _idToken: string,
  submit: Record<string, unknown>
) => {
  return await axios.post<{ order: CheckoutOrder }>(
    `${orchestration.url}/delivery/orders/${orderId}/submit`,
    {
      submit
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

export interface LeasingCalculator {
  kmPerYear: number;
  leasingPeriod: number;
}
export const getLeasingCost = async (
  orderId: number,
  _idToken: string,
  leasingCalculator: LeasingCalculator
) => {
  return await axios.post<CheckoutOrder>(
    `${orchestration.url}/delivery/orders/${orderId}/calculator/leasing`,
    {
      leasingCalculator
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

export interface GetLoanCostInput {
  orderId: number;
  idToken: string;
  price: number;
  downPayment: number;
  loanPeriod: number;
  residualValuePercentage: number;
}
export const getLoanCost = async ({
  idToken,
  orderId,
  ...data
}: GetLoanCostInput) => {
  return await axios.post<CheckoutOrder>(
    `${orchestration.url}/delivery/orders/${orderId}/calculator/loan`,
    {
      loanCalculator: data
    },
    {
      responseType: 'json',
      withCredentials: true
    }
  );
};

export type CheckoutError = unknown | AxiosError;
